import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Building2, Users, BarChart3, Plus, Search,
    Shield, ShieldOff, Trash2, Eye, Edit2, Loader2,
    TrendingUp, UserCheck, ContactRound, Target, Layers,
    DollarSign, ArrowUpRight, ArrowDownRight, Mail, KeyRound,
    UserX, UserPlus, ChevronDown, ScrollText, UserCog, Database, AlertTriangle,
    Gift, Calendar, Bug, Sparkles, MessageSquare, Paperclip, Settings,
    Check, X, Filter, User, ThumbsUp, Clock, Brain, Zap, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { id: 'organizations', label: 'Organizations', icon: Building2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'platform_admins', label: 'Platform Admins', icon: Shield },
    { id: 'features', label: 'Feature Requests', icon: Target },
    { id: 'usage', label: 'Usage & Billing', icon: BarChart3 },
    { id: 'ai_usage', label: 'AI Usage', icon: Brain },
    { id: 'service_usage', label: 'Service Costs', icon: DollarSign },
    { id: 'audit', label: 'Audit Logs', icon: ScrollText },
    { id: 'data', label: 'Data Management', icon: Database },
];

const planColors = {
    free: 'bg-stone-100 text-stone-700',
    starter: 'bg-blue-100 text-blue-700',
    pro: 'bg-stone-100 text-stone-900',
    professional: 'bg-stone-100 text-stone-900',
    enterprise: 'bg-amber-100 text-amber-700',
};
const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    suspended: 'bg-red-100 text-red-700',
    trial: 'bg-amber-100 text-amber-700',
};
const roleColors = {
    admin: 'bg-stone-100 text-stone-900',
    manager: 'bg-blue-100 text-blue-700',
    sales_rep: 'bg-stone-100 text-stone-700',
};

export default function SuperAdminDashboard() {
    const { getAuthHeader, user } = useAuth();
    const [activeTab, setActiveTab] = useState('organizations');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadStats = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/super-admin/stats`, { headers: getAuthHeader() });
            setStats(res.data);
        } catch {}
        setLoading(false);
    }, [getAuthHeader]);

    useEffect(() => { loadStats(); }, [loadStats]);

    if (!user?.is_super_admin) {
        return (
            <div className="flex items-center justify-center h-full" data-testid="super-admin-denied">
                <div className="text-center">
                    <Shield className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-stone-700">Access Denied</h2>
                    <p className="text-stone-500 mt-1">Super admin privileges required</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="super-admin-dashboard">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Platform Administration</h1>
                    <p className="text-sm text-stone-500 mt-1">Manage organizations, users, and monitor platform health</p>
                </div>
            </div>

            {/* Stats Bar */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="platform-stats">
                    {[
                        { label: 'Organizations', value: stats.total_organizations, icon: Building2, color: 'text-stone-700 bg-stone-100' },
                        { label: 'Active Orgs', value: stats.active_organizations, icon: UserCheck, color: 'text-emerald-600 bg-emerald-50' },
                        { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50' },
                        { label: 'Contacts', value: stats.total_contacts, icon: ContactRound, color: 'text-purple-600 bg-purple-50' },
                        { label: 'Companies', value: stats.total_accounts, icon: Building2, color: 'text-cyan-600 bg-cyan-50' },
                        { label: 'Deals', value: stats.total_deals, icon: Target, color: 'text-amber-600 bg-amber-50' },
                        { label: 'Sequences', value: stats.total_sequences, icon: Layers, color: 'text-rose-600 bg-rose-50' },
                    ].map(s => (
                        <div key={s.label} className="bg-white rounded-lg border border-stone-200 p-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${s.color}`}>
                                <s.icon className="w-4 h-4" />
                            </div>
                            <div className="text-xl font-bold text-stone-900">{s.value?.toLocaleString()}</div>
                            <div className="text-xs text-stone-500">{s.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="border-b border-stone-200" data-testid="admin-tabs">
                <nav className="flex gap-6">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            data-testid={`tab-${tab.id}`}
                            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.id
                                    ? 'border-stone-700 text-stone-700'
                                    : 'border-transparent text-stone-500 hover:text-stone-700'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {activeTab === 'organizations' && <OrganizationsTab getAuthHeader={getAuthHeader} onRefresh={loadStats} />}
            {activeTab === 'users' && <UsersTab getAuthHeader={getAuthHeader} />}
            {activeTab === 'platform_admins' && <PlatformAdminsTab getAuthHeader={getAuthHeader} currentUser={user} />}
            {activeTab === 'features' && <FeatureRequestsAdminTab getAuthHeader={getAuthHeader} />}
            {activeTab === 'usage' && <UsageBillingTab getAuthHeader={getAuthHeader} />}
            {activeTab === 'ai_usage' && <AIUsageTab getAuthHeader={getAuthHeader} />}
            {activeTab === 'service_usage' && <ServiceUsageTab getAuthHeader={getAuthHeader} />}
            {activeTab === 'audit' && <AuditLogsTab getAuthHeader={getAuthHeader} />}
            {activeTab === 'data' && <DataManagementTab getAuthHeader={getAuthHeader} onRefresh={loadStats} />}
        </div>
    );
}

const ALL_MODULES = [
    { key: 'ats', label: 'Recruitment (ATS)', description: 'Applicant tracking, CV parsing, AI matching' },
    { key: 'hris', label: 'HR Management (HRIS)', description: 'Employee records, onboarding, time tracking' },
    { key: 'finance', label: 'Finance', description: 'Invoicing, billing, expense management' },
    { key: 'projects', label: 'Projects', description: 'Project management and resource planning' },
];

const ALL_FEATURES = [
    { key: 'ai_assistant', label: 'AI Assistant' },
    { key: 'ai_heartbeat', label: 'AI Heartbeat' },
    { key: 'email_sequences', label: 'Email Sequences' },
    { key: 'deal_rooms', label: 'Deal Rooms' },
    { key: 'booking_links', label: 'Booking Links' },
    { key: 'proposals', label: 'Proposals' },
    { key: 'products', label: 'Products' },
    { key: 'list_management', label: 'List Management' },
    { key: 'custom_domains', label: 'Custom Domains' },
    { key: 'reports', label: 'Reports' },
    { key: 'sites', label: 'Sites' },
    { key: 'agentic_personalization', label: 'Agentic Personalization' },
    { key: 'floating_ai', label: 'Floating AI Assistant' },
];

const ALL_LIMITS = [
    { key: 'users', label: 'Users' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'accounts', label: 'Companies' },
    { key: 'deals', label: 'Deals' },
    { key: 'booking_links', label: 'Booking Links' },
    { key: 'email_sequences', label: 'Sequences' },
    { key: 'deal_rooms', label: 'Deal Rooms' },
    { key: 'heartbeats', label: 'Heartbeats' },
];

/* ─── Organizations Tab ──────────────────────────────────────────────────── */
function OrganizationsTab({ getAuthHeader, onRefresh }) {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterPlan, setFilterPlan] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({
        name: '', admin_email: '', admin_name: '', admin_password: '',
        domain: '', industry: '', plan: 'free', max_users: 5, max_contacts: 1000,
    });
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});

    const loadOrgs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterPlan) params.set('plan', filterPlan);
            if (filterStatus) params.set('status', filterStatus);
            const res = await axios.get(`${API}/super-admin/organizations?${params}`, { headers: getAuthHeader() });
            setOrgs(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { toast.error('Failed to load organizations'); }
        finally { setLoading(false); }
    }, [getAuthHeader, search, filterPlan, filterStatus]);

    useEffect(() => { loadOrgs(); }, [loadOrgs]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!createForm.name || !createForm.admin_email || !createForm.admin_password) {
            toast.error('Fill in all required fields'); return;
        }
        setCreating(true);
        try {
            await axios.post(`${API}/super-admin/organizations`, createForm, { headers: getAuthHeader() });
            toast.success(`Organization "${createForm.name}" created`);
            setShowCreate(false);
            setCreateForm({ name: '', admin_email: '', admin_name: '', admin_password: '', domain: '', industry: '', plan: 'free', max_users: 5, max_contacts: 1000 });
            loadOrgs(); onRefresh();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
        finally { setCreating(false); }
    };

    const loadOrgDetail = async (orgId) => {
        setDetailLoading(true); setShowDetail(true);
        try {
            const res = await axios.get(`${API}/super-admin/organizations/${orgId}`, { headers: getAuthHeader() });
            setSelectedOrg(res.data);
            setEditForm({ name: res.data.name, plan: res.data.plan, max_users: res.data.max_users, max_contacts: res.data.max_contacts, industry: res.data.industry || '', domain: res.data.domain || '', size: res.data.size || '', is_complimentary: res.data.is_complimentary || false, plan_expires_at: res.data.plan_expires_at || null, feature_overrides: res.data.feature_overrides || {}, limit_overrides: res.data.limit_overrides || {}, enabled_modules: res.data.enabled_modules || [] });
        } catch { toast.error('Failed to load'); setShowDetail(false); }
        finally { setDetailLoading(false); }
    };

    const handleUpdate = async () => {
        if (!selectedOrg) return;
        try {
            await axios.put(`${API}/super-admin/organizations/${selectedOrg.org_id}`, editForm, { headers: getAuthHeader() });
            toast.success('Organization updated'); setEditing(false);
            loadOrgDetail(selectedOrg.org_id); loadOrgs(); onRefresh();
        } catch { toast.error('Failed to update'); }
    };

    const handleSuspend = async (orgId, orgName) => {
        if (!window.confirm(`Suspend "${orgName}"? All users will lose access.`)) return;
        try {
            await axios.post(`${API}/super-admin/organizations/${orgId}/suspend`, {}, { headers: getAuthHeader() });
            toast.success(`"${orgName}" suspended`); loadOrgs();
            if (showDetail) loadOrgDetail(orgId);
        } catch { toast.error('Failed to suspend'); }
    };

    const handleActivate = async (orgId, orgName) => {
        try {
            await axios.post(`${API}/super-admin/organizations/${orgId}/activate`, {}, { headers: getAuthHeader() });
            toast.success(`"${orgName}" activated`); loadOrgs();
            if (showDetail) loadOrgDetail(orgId);
        } catch { toast.error('Failed to activate'); }
    };

    const handleDelete = async (orgId, orgName) => {
        if (!window.confirm(`DELETE "${orgName}" and ALL its data? This cannot be undone.`)) return;
        if (!window.confirm(`Are you absolutely sure?`)) return;
        try {
            await axios.delete(`${API}/super-admin/organizations/${orgId}`, { headers: getAuthHeader() });
            toast.success(`"${orgName}" deleted`); setShowDetail(false); loadOrgs(); onRefresh();
        } catch { toast.error('Failed to delete'); }
    };

    return (
        <>
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search organizations..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                            data-testid="org-search-input" />
                    </div>
                    <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="plan-filter">
                        <option value="">All Plans</option>
                        <option value="free">Free</option><option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
                    </select>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="status-filter">
                        <option value="">All Statuses</option>
                        <option value="active">Active</option><option value="suspended">Suspended</option>
                    </select>
                    <Button onClick={() => setShowCreate(true)} data-testid="create-org-btn" size="sm">
                        <Plus className="w-4 h-4 mr-1" /> New Organization
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-stone-600 animate-spin" /></div>
                ) : orgs.length === 0 ? (
                    <div className="text-center py-16"><Building2 className="w-12 h-12 text-stone-200 mx-auto mb-3" /><p className="text-stone-500">No organizations found</p></div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]" data-testid="org-table">
                        <thead>
                            <tr className="bg-stone-50 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                                <th className="px-5 py-3">Organization</th>
                                <th className="px-5 py-3">Plan</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3 text-center">Users</th>
                                <th className="px-5 py-3 text-center">Contacts</th>
                                <th className="px-5 py-3 text-center">Deals</th>
                                <th className="px-5 py-3">Created</th>
                                <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {orgs.map(org => (
                                <tr key={org.org_id} className="hover:bg-stone-50 transition-colors cursor-pointer" onClick={() => loadOrgDetail(org.org_id)} data-testid={`org-row-${org.org_id}`}>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center text-stone-700 font-bold text-sm">
                                                {org.name?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-stone-900">{org.name}</div>
                                                <div className="text-xs text-stone-400">{org.domain || '-'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-3"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${planColors[org.plan] || planColors.free}`}>{org.plan || 'free'}</span></td>
                                    <td className="px-5 py-3"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[org.status] || statusColors.active}`}>{org.status || 'active'}</span></td>
                                    <td className="px-5 py-3 text-center text-sm text-stone-600">{org.user_count}</td>
                                    <td className="px-5 py-3 text-center text-sm text-stone-600">{org.contact_count?.toLocaleString()}</td>
                                    <td className="px-5 py-3 text-center text-sm text-stone-600">{org.deal_count}</td>
                                    <td className="px-5 py-3 text-xs text-stone-400">{org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}</td>
                                    <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => { loadOrgDetail(org.org_id); setTimeout(() => setEditing(true), 300); }} className="p-1.5 text-stone-400 hover:text-blue-600 transition-colors" title="Edit organization"><Edit2 className="w-4 h-4" /></button>
                                            <button onClick={() => loadOrgDetail(org.org_id)} className="p-1.5 text-stone-400 hover:text-stone-700 transition-colors" title="View details" data-testid={`view-org-${org.org_id}`}><Eye className="w-4 h-4" /></button>
                                            {org.status === 'active' ? (
                                                <button onClick={() => handleSuspend(org.org_id, org.name)} className="p-1.5 text-stone-400 hover:text-amber-600 transition-colors" title="Suspend" data-testid={`suspend-org-${org.org_id}`}><ShieldOff className="w-4 h-4" /></button>
                                            ) : (
                                                <button onClick={() => handleActivate(org.org_id, org.name)} className="p-1.5 text-stone-400 hover:text-emerald-600 transition-colors" title="Activate" data-testid={`activate-org-${org.org_id}`}><Shield className="w-4 h-4" /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            {/* Create Organization Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create New Organization</DialogTitle>
                        <DialogDescription>Set up a new company account with an admin user</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4" data-testid="create-org-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-stone-600 mb-1">Organization Name *</label>
                                <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    placeholder="Acme Corp" data-testid="create-org-name" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Admin Email *</label>
                                <input type="email" value={createForm.admin_email} onChange={e => setCreateForm(f => ({ ...f, admin_email: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    placeholder="admin@acme.com" data-testid="create-org-email" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Admin Name</label>
                                <input value={createForm.admin_name} onChange={e => setCreateForm(f => ({ ...f, admin_name: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    placeholder="John Admin" data-testid="create-org-admin-name" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Admin Password *</label>
                                <input type="password" value={createForm.admin_password} onChange={e => setCreateForm(f => ({ ...f, admin_password: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    placeholder="Min 8 characters" data-testid="create-org-password" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Domain</label>
                                <input value={createForm.domain} onChange={e => setCreateForm(f => ({ ...f, domain: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    placeholder="acme.com" data-testid="create-org-domain" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Plan</label>
                                <select value={createForm.plan} onChange={e => setCreateForm(f => ({ ...f, plan: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="create-org-plan">
                                    <option value="free">Free</option><option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Max Users</label>
                                <input type="number" value={createForm.max_users} onChange={e => setCreateForm(f => ({ ...f, max_users: parseInt(e.target.value) || 5 }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" data-testid="create-org-max-users" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button type="submit" disabled={creating} data-testid="create-org-submit">
                                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                                Create Organization
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Organization Detail Modal */}
            <Dialog open={showDetail} onOpenChange={setShowDetail}>
                <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
                    {detailLoading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-600 animate-spin" /></div>
                    ) : selectedOrg && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-stone-700 font-bold">
                                        {selectedOrg.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div>
                                        {selectedOrg.name}
                                        <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[selectedOrg.status] || statusColors.active}`}>
                                            {selectedOrg.status}
                                        </span>
                                    </div>
                                </DialogTitle>
                                <DialogDescription>
                                    {selectedOrg.domain || 'No domain'} | Plan: {selectedOrg.plan} | Created: {new Date(selectedOrg.created_at).toLocaleDateString()}
                                    {selectedOrg.is_complimentary && selectedOrg.plan_expires_at && (
                                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                                            <Gift className="w-3 h-3" /> Complimentary until {new Date(selectedOrg.plan_expires_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </DialogDescription>
                            </DialogHeader>

                            {selectedOrg.stats && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 my-3" data-testid="org-detail-stats">
                                    {Object.entries(selectedOrg.stats).map(([k, v]) => (
                                        <div key={k} className="bg-stone-50 rounded-lg p-2.5 text-center">
                                            <div className="text-lg font-bold text-stone-900">{v}</div>
                                            <div className="text-xs text-stone-500 capitalize">{k}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Effective Features (read-only display) */}
                            {!editing && selectedOrg.plan_defaults && (
                                <div className="border-t border-stone-100 pt-3">
                                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Features</h4>
                                    <div className="flex flex-wrap gap-1.5">
                                        {ALL_FEATURES.map(f => {
                                            const planDefault = selectedOrg.plan_defaults?.features?.[f.key];
                                            const override = (selectedOrg.feature_overrides || {})[f.key];
                                            const effective = override !== undefined ? override : planDefault;
                                            const isOverridden = override !== undefined;
                                            return (
                                                <span key={f.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    effective ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-400'
                                                } ${isOverridden ? 'ring-1 ring-amber-400' : ''}`}>
                                                    {effective ? '✓' : '✗'} {f.label}
                                                    {isOverridden && <span className="text-amber-500 text-[10px]">⚡</span>}
                                                </span>
                                            );
                                        })}
                                    </div>
                                    {/* ERP Modules (read-only) */}
                                    <div className="mt-3">
                                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">ERP Modules</h4>
                                        <div className="flex flex-wrap gap-1.5">
                                            {ALL_MODULES.map(m => {
                                                const enabled = (selectedOrg.enabled_modules || []).includes(m.key);
                                                return (
                                                    <span key={m.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        enabled ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'bg-stone-100 text-stone-400'
                                                    }`}>
                                                        {enabled ? '✓' : '✗'} {m.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {editing ? (
                                <div className="border-t border-stone-100 pt-3" data-testid="edit-org-form">
                                    <div className="bg-stone-50 rounded-lg p-4 space-y-4">
                                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Edit Organisation</h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Name</label>
                                                <input value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" /></div>
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Domain</label>
                                                <input value={editForm.domain || ''} onChange={e => setEditForm(f => ({ ...f, domain: e.target.value }))} placeholder="company.com" className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" /></div>
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Industry</label>
                                                <input value={editForm.industry || ''} onChange={e => setEditForm(f => ({ ...f, industry: e.target.value }))} placeholder="e.g. Technology" className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" /></div>
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Size</label>
                                                <select value={editForm.size || ''} onChange={e => setEditForm(f => ({ ...f, size: e.target.value }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500">
                                                    <option value="">Select size</option>
                                                    <option value="1-10">1-10</option><option value="11-50">11-50</option><option value="51-200">51-200</option>
                                                    <option value="201-500">201-500</option><option value="501-1000">501-1000</option><option value="1001-5000">1001-5000</option><option value="5000+">5000+</option>
                                                </select></div>
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Plan</label>
                                                <select value={editForm.plan || 'free'} onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500">
                                                    <option value="free">Free</option><option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
                                                </select></div>
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Max Users</label>
                                                <input type="number" value={editForm.max_users || 5} onChange={e => setEditForm(f => ({ ...f, max_users: parseInt(e.target.value) || 5 }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" /></div>
                                            <div><label className="block text-xs font-medium text-stone-600 mb-1">Max Contacts</label>
                                                <input type="number" value={editForm.max_contacts || 1000} onChange={e => setEditForm(f => ({ ...f, max_contacts: parseInt(e.target.value) || 1000 }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" /></div>
                                        </div>

                                        {/* Complimentary Access Section */}
                                        <div className="border-t border-stone-200 pt-4 mt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Gift className="w-4 h-4 text-stone-500" />
                                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Complimentary Access</h4>
                                            </div>
                                            <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
                                                <input type="checkbox" checked={editForm.is_complimentary || false}
                                                    onChange={e => {
                                                        const checked = e.target.checked;
                                                        setEditForm(f => ({
                                                            ...f,
                                                            is_complimentary: checked,
                                                            plan_expires_at: checked ? f.plan_expires_at : null,
                                                            plan: checked && f.plan === 'free' ? 'pro' : f.plan
                                                        }));
                                                    }}
                                                    className="rounded border-stone-300" />
                                                <span className="text-stone-700">Grant no-cost access to a paid plan</span>
                                            </label>
                                            {editForm.is_complimentary && (
                                                <div className="space-y-3 pl-6">
                                                    <div className="flex flex-wrap gap-2">
                                                        {[
                                                            { label: '30 days', days: 30 },
                                                            { label: '60 days', days: 60 },
                                                            { label: '90 days', days: 90 },
                                                            { label: '1 year', days: 365 },
                                                        ].map(opt => {
                                                            const target = new Date(Date.now() + opt.days * 86400000).toISOString().split('T')[0];
                                                            const current = editForm.plan_expires_at?.split('T')[0];
                                                            return (
                                                                <button key={opt.days} type="button"
                                                                    onClick={() => setEditForm(f => ({ ...f, plan_expires_at: new Date(Date.now() + opt.days * 86400000).toISOString() }))}
                                                                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${current === target ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}>
                                                                    {opt.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-stone-600 mb-1">Expires on</label>
                                                        <input type="date"
                                                            value={editForm.plan_expires_at ? editForm.plan_expires_at.split('T')[0] : ''}
                                                            onChange={e => setEditForm(f => ({ ...f, plan_expires_at: e.target.value ? new Date(e.target.value + 'T23:59:59Z').toISOString() : null }))}
                                                            min={new Date().toISOString().split('T')[0]}
                                                            className="w-full max-w-[200px] px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Feature Overrides */}
                                        <div className="border-t border-stone-200 pt-4 mt-4">
                                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Feature Overrides</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {ALL_FEATURES.map(f => {
                                                    const planDefault = selectedOrg?.plan_defaults?.features?.[f.key];
                                                    const override = (editForm.feature_overrides || {})[f.key];
                                                    const state = override === undefined ? 'default' : override ? 'on' : 'off';
                                                    return (
                                                        <div key={f.key} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-stone-200">
                                                            <div>
                                                                <span className="text-sm text-stone-700">{f.label}</span>
                                                                <span className={`ml-2 text-[10px] ${planDefault ? 'text-emerald-500' : 'text-stone-400'}`}>
                                                                    Plan: {planDefault ? '✓' : '✗'}
                                                                </span>
                                                            </div>
                                                            <div className="flex gap-0.5">
                                                                {[
                                                                    { val: 'default', label: 'Default', color: 'bg-stone-200 text-stone-600' },
                                                                    { val: 'on', label: 'On', color: 'bg-emerald-500 text-white' },
                                                                    { val: 'off', label: 'Off', color: 'bg-red-500 text-white' },
                                                                ].map(opt => (
                                                                    <button key={opt.val} type="button"
                                                                        onClick={() => {
                                                                            const newOverrides = { ...(editForm.feature_overrides || {}) };
                                                                            if (opt.val === 'default') delete newOverrides[f.key];
                                                                            else newOverrides[f.key] = opt.val === 'on';
                                                                            setEditForm(ef => ({ ...ef, feature_overrides: newOverrides }));
                                                                        }}
                                                                        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                                                                            state === opt.val ? opt.color : 'bg-stone-100 text-stone-400 hover:bg-stone-200'
                                                                        }`}>
                                                                        {opt.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* ERP Modules */}
                                        <div className="border-t border-stone-200 pt-4 mt-4">
                                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">ERP Modules</h4>
                                            <div className="space-y-2">
                                                {ALL_MODULES.map(m => {
                                                    const enabled = (editForm.enabled_modules || []).includes(m.key);
                                                    return (
                                                        <div key={m.key} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                                                            enabled ? 'bg-blue-50 border-blue-200' : 'bg-white border-stone-200'
                                                        }`}>
                                                            <div>
                                                                <span className="text-sm font-medium text-stone-700">{m.label}</span>
                                                                <p className="text-[10px] text-stone-400">{m.description}</p>
                                                            </div>
                                                            <div className="flex gap-0.5">
                                                                {[
                                                                    { val: true, label: 'On', color: 'bg-blue-500 text-white' },
                                                                    { val: false, label: 'Off', color: 'bg-stone-200 text-stone-600' },
                                                                ].map(opt => (
                                                                    <button key={String(opt.val)} type="button"
                                                                        onClick={() => {
                                                                            const current = editForm.enabled_modules || [];
                                                                            const newModules = opt.val
                                                                                ? [...new Set([...current, m.key])]
                                                                                : current.filter(x => x !== m.key);
                                                                            setEditForm(ef => ({ ...ef, enabled_modules: newModules }));
                                                                        }}
                                                                        className={`px-2.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                                                                            enabled === opt.val ? opt.color : 'bg-stone-100 text-stone-400 hover:bg-stone-200'
                                                                        }`}>
                                                                        {opt.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Limit Overrides */}
                                        <div className="border-t border-stone-200 pt-4 mt-4">
                                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Limit Overrides</h4>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                {ALL_LIMITS.map(l => {
                                                    const planDefault = selectedOrg?.plan_defaults?.limits?.[l.key];
                                                    const override = (editForm.limit_overrides || {})[l.key];
                                                    const hasOverride = override !== undefined;
                                                    return (
                                                        <div key={l.key}>
                                                            <label className="block text-xs text-stone-600 mb-1">
                                                                {l.label}
                                                                <span className="text-stone-400 ml-1">(plan: {planDefault === -1 ? '∞' : planDefault})</span>
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={hasOverride ? override : ''}
                                                                onChange={e => {
                                                                    const newOverrides = { ...(editForm.limit_overrides || {}) };
                                                                    if (e.target.value === '') delete newOverrides[l.key];
                                                                    else newOverrides[l.key] = parseInt(e.target.value) || 0;
                                                                    setEditForm(ef => ({ ...ef, limit_overrides: newOverrides }));
                                                                }}
                                                                placeholder={planDefault === -1 ? '∞' : String(planDefault)}
                                                                className={`w-full px-2 py-1.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 ${
                                                                    hasOverride ? 'border-amber-400' : 'border-stone-200'
                                                                }`}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-stone-400 mt-1">Leave empty to use plan default. Set -1 for unlimited.</p>
                                        </div>

                                        <div className="flex gap-2 mt-4">
                                            <Button size="sm" onClick={handleUpdate}>Save Changes</Button>
                                            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {selectedOrg.members?.length > 0 && (
                                <div className="border-t border-stone-100 pt-3">
                                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Members ({selectedOrg.members.length})</h4>
                                    <div className="space-y-1.5">
                                        {selectedOrg.members.map(m => (
                                            <div key={m.user_id} className="flex items-center justify-between px-3 py-2 bg-stone-50 rounded-lg text-sm">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium text-stone-900">
                                                        {m.name?.charAt(0)?.toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-stone-900">{m.name}</span>
                                                        <span className="text-stone-400 ml-1.5 text-xs">{m.email}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-stone-400">{m.org_role || m.role}</span>
                                                    {m.is_admin && <span className="text-xs bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">Admin</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-stone-100">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selectedOrg.status === 'active' ? (
                                        <Button variant="outline" size="sm" className="text-amber-600" onClick={() => handleSuspend(selectedOrg.org_id, selectedOrg.name)}>
                                            <ShieldOff className="w-3.5 h-3.5 mr-1" /> Suspend
                                        </Button>
                                    ) : (
                                        <Button variant="outline" size="sm" className="text-emerald-600" onClick={() => handleActivate(selectedOrg.org_id, selectedOrg.name)}>
                                            <Shield className="w-3.5 h-3.5 mr-1" /> Activate
                                        </Button>
                                    )}
                                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(selectedOrg.org_id, selectedOrg.name)} data-testid="delete-org-btn">
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-org-btn">
                                        <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setShowDetail(false)}>Close</Button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

/* ─── Users Tab ───────────────────────────────────────────────────────────── */
function UsersTab({ getAuthHeader }) {
    const [users, setUsers] = useState([]);
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterOrg, setFilterOrg] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Create user modal
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', org_id: '', role: 'sales_rep' });

    // User detail modal
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editingUser, setEditingUser] = useState(false);
    const [editUserForm, setEditUserForm] = useState({});

    // Reset password modal
    const [showResetPw, setShowResetPw] = useState(false);
    const [resetPwUserId, setResetPwUserId] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [resetting, setResetting] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterOrg) params.set('org_id', filterOrg);
            if (filterRole) params.set('role', filterRole);
            if (filterStatus) params.set('status', filterStatus);

            const [usersRes, orgsRes] = await Promise.all([
                axios.get(`${API}/super-admin/users?${params}`, { headers: getAuthHeader() }),
                axios.get(`${API}/super-admin/organizations`, { headers: getAuthHeader() }),
            ]);
            setUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.data || []));
            setOrgs(Array.isArray(orgsRes.data) ? orgsRes.data : (orgsRes.data?.data || []));
        } catch { toast.error('Failed to load users'); }
        finally { setLoading(false); }
    }, [getAuthHeader, search, filterOrg, filterRole, filterStatus]);

    useEffect(() => { loadData(); }, [loadData]);

    const loadUserDetail = async (userId) => {
        setDetailLoading(true); setShowDetail(true);
        try {
            const res = await axios.get(`${API}/super-admin/users/${userId}`, { headers: getAuthHeader() });
            setSelectedUser(res.data);
            setEditUserForm({ name: res.data.name, role: res.data.role, is_active: res.data.is_active, is_admin: res.data.is_admin, org_role: res.data.org_role || 'member' });
        } catch { toast.error('Failed to load user'); setShowDetail(false); }
        finally { setDetailLoading(false); }
    };

    const handleUpdateUser = async () => {
        if (!selectedUser) return;
        try {
            await axios.put(`${API}/super-admin/users/${selectedUser.user_id}`, editUserForm, { headers: getAuthHeader() });
            toast.success('User updated'); setEditingUser(false);
            loadUserDetail(selectedUser.user_id); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update user'); }
    };

    const handleDeleteUser = async (userId, email) => {
        if (!window.confirm(`Delete user "${email}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/super-admin/users/${userId}`, { headers: getAuthHeader() });
            toast.success('User deleted'); setShowDetail(false); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    };

    const handleImpersonate = async (targetUser) => {
        if (!window.confirm(`Impersonate "${targetUser.name || targetUser.email}"?\n\nYou will be logged in as this user. Open the app in a new tab to use the impersonated session.`)) return;
        try {
            const res = await axios.post(`${API}/super-admin/impersonate/${targetUser.user_id}`, {}, { headers: getAuthHeader() });
            const { access_token, impersonated_user } = res.data;
            // Open in new tab with the impersonation token
            const impersonateUrl = `${window.location.origin}/dashboard?impersonate_token=${access_token}&impersonate_name=${encodeURIComponent(impersonated_user.name || impersonated_user.email)}`;
            window.open(impersonateUrl, '_blank');
            toast.success(`Impersonation session opened for ${impersonated_user.name || impersonated_user.email}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to impersonate'); }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        setResetting(true);
        try {
            await axios.put(`${API}/super-admin/users/${resetPwUserId}/reset-password`, { new_password: newPassword }, { headers: getAuthHeader() });
            toast.success('Password reset successfully'); setShowResetPw(false); setNewPassword('');
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
        finally { setResetting(false); }
    };

    // Alternative to setting the password directly — emails the user a
    // 24h reset link they can use to set their own password. Backend
    // generates a random url-safe token, stores it on the user record,
    // and dispatches via Resend.
    const sendResetEmail = async (u) => {
        const ok = window.confirm(
            `Send a password-reset email to ${u.email}?\n\n` +
            `They'll get a link (valid 24h) to set their own password. ` +
            `This is the recommended option vs. handing them a password directly.`
        );
        if (!ok) return;
        try {
            const res = await axios.post(
                `${API}/super-admin/users/${u.user_id}/send-reset-email`,
                {},
                { headers: getAuthHeader() }
            );
            if (res.data?.delivered === false) {
                toast.warning(`Token created but email failed — check logs. ${u.email} can still use the next reset.`);
            } else {
                toast.success(`Reset email sent to ${u.email}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send reset email');
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!createForm.email || !createForm.password || !createForm.org_id) { toast.error('Fill in all required fields'); return; }
        setCreating(true);
        try {
            await axios.post(`${API}/super-admin/users`, createForm, { headers: getAuthHeader() });
            toast.success('User created'); setShowCreate(false);
            setCreateForm({ name: '', email: '', password: '', org_id: '', role: 'sales_rep' }); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
        finally { setCreating(false); }
    };

    const toggleUserActive = async (userId, currentActive) => {
        try {
            await axios.put(`${API}/super-admin/users/${userId}`, { is_active: !currentActive }, { headers: getAuthHeader() });
            toast.success(currentActive ? 'User deactivated' : 'User activated'); loadData();
        } catch { toast.error('Failed to update user status'); }
    };

    return (
        <>
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                            data-testid="user-search-input" />
                    </div>
                    <select value={filterOrg} onChange={e => setFilterOrg(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg max-w-[180px]" data-testid="user-org-filter">
                        <option value="">All Organizations</option>
                        {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
                    </select>
                    <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="user-role-filter">
                        <option value="">All Roles</option>
                        <option value="admin">Admin</option><option value="manager">Manager</option><option value="sales_rep">Sales Rep</option>
                    </select>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="user-status-filter">
                        <option value="">All</option>
                        <option value="active">Active</option><option value="inactive">Inactive</option>
                    </select>
                    <Button onClick={() => setShowCreate(true)} size="sm" data-testid="create-user-btn">
                        <UserPlus className="w-4 h-4 mr-1" /> New User
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-stone-600 animate-spin" /></div>
                ) : users.length === 0 ? (
                    <div className="text-center py-16"><Users className="w-12 h-12 text-stone-200 mx-auto mb-3" /><p className="text-stone-500">No users found</p></div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]" data-testid="users-table">
                        <thead>
                            <tr className="bg-stone-50 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                                <th className="px-5 py-3">User</th>
                                <th className="px-5 py-3">Organization</th>
                                <th className="px-5 py-3">Role</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Joined</th>
                                <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {users.map(u => (
                                <tr key={u.user_id} className="hover:bg-stone-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${u.is_active ? 'bg-stone-100 text-stone-900' : 'bg-stone-100 text-stone-400'}`}>
                                                {u.name?.charAt(0)?.toUpperCase() || '?'}
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-stone-900">{u.name || 'Unnamed'}</div>
                                                <div className="text-xs text-stone-400">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-3 text-sm text-stone-600">{u.org_name}</td>
                                    <td className="px-5 py-3">
                                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColors[u.role] || roleColors.sales_rep}`}>
                                            {u.role?.replace('_', ' ')}
                                        </span>
                                        {u.is_admin && <span className="ml-1 inline-flex px-1.5 py-0.5 text-xs bg-stone-100 text-stone-700 rounded">Admin</span>}
                                    </td>
                                    <td className="px-5 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                            {u.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-xs text-stone-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                                    <td className="px-5 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => loadUserDetail(u.user_id)} className="p-1.5 text-stone-400 hover:text-stone-700 transition-colors" data-testid={`view-user-${u.user_id}`}><Eye className="w-4 h-4" /></button>
                                            <button onClick={() => toggleUserActive(u.user_id, u.is_active)}
                                                className={`p-1.5 transition-colors ${u.is_active ? 'text-stone-400 hover:text-red-500' : 'text-stone-400 hover:text-emerald-600'}`}
                                                data-testid={`toggle-user-${u.user_id}`}
                                                title={u.is_active ? 'Deactivate' : 'Activate'}
                                            >
                                                {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                            </button>
                                            <button onClick={() => { setResetPwUserId(u.user_id); setShowResetPw(true); }}
                                                className="p-1.5 text-stone-400 hover:text-amber-600 transition-colors" data-testid={`reset-pw-${u.user_id}`} title="Reset Password (set directly)">
                                                <KeyRound className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => sendResetEmail(u)}
                                                className="p-1.5 text-stone-400 hover:text-violet-600 transition-colors" data-testid={`send-reset-email-${u.user_id}`} title="Send reset password email">
                                                <Mail className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            {/* Create User Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New User</DialogTitle>
                        <DialogDescription>Add a user to any organization</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4" data-testid="create-user-form">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Organization *</label>
                                <select value={createForm.org_id} onChange={e => setCreateForm(f => ({ ...f, org_id: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="create-user-org">
                                    <option value="">Select organization...</option>
                                    {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-stone-600 mb-1">Name</label>
                                    <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" placeholder="John Smith" data-testid="create-user-name" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-stone-600 mb-1">Role</label>
                                    <select value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="create-user-role">
                                        <option value="sales_rep">Sales Rep</option><option value="manager">Manager</option><option value="admin">Admin</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Email *</label>
                                <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" placeholder="user@company.com" data-testid="create-user-email" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Password *</label>
                                <input type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" placeholder="Min 8 characters" data-testid="create-user-password" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button type="submit" disabled={creating} data-testid="create-user-submit">
                                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                                Create User
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* User Detail Modal */}
            <Dialog open={showDetail} onOpenChange={setShowDetail}>
                <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
                    {detailLoading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-600 animate-spin" /></div>
                    ) : selectedUser && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${selectedUser.is_active ? 'bg-stone-100 text-stone-900' : 'bg-stone-200 text-stone-500'}`}>
                                        {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                    <div>
                                        <div>{selectedUser.name || 'Unnamed'}</div>
                                        <div className="text-sm font-normal text-stone-400">{selectedUser.email}</div>
                                    </div>
                                </DialogTitle>
                            </DialogHeader>

                            <div className="space-y-4">
                                {/* Info Cards */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <div className="text-xs text-stone-500 mb-1">Organization</div>
                                        <div className="text-sm font-medium text-stone-900">{selectedUser.organization?.name || 'Unknown'}</div>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <div className="text-xs text-stone-500 mb-1">Role</div>
                                        <div className="text-sm font-medium text-stone-900 capitalize">{selectedUser.role?.replace('_', ' ')}</div>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <div className="text-xs text-stone-500 mb-1">Status</div>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${selectedUser.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {selectedUser.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="bg-stone-50 rounded-lg p-3">
                                        <div className="text-xs text-stone-500 mb-1">Org Role</div>
                                        <div className="text-sm font-medium text-stone-900 capitalize">{selectedUser.org_role || 'member'}</div>
                                    </div>
                                </div>

                                {/* Activity Stats */}
                                {selectedUser.activity_stats && (
                                    <div>
                                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Activity</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            {Object.entries(selectedUser.activity_stats).map(([k, v]) => (
                                                <div key={k} className="bg-white border border-stone-200 rounded-lg p-2.5 text-center">
                                                    <div className="text-lg font-bold text-stone-900">{v}</div>
                                                    <div className="text-xs text-stone-500 capitalize">{k.replace('_', ' ')}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Edit Form */}
                                {editingUser && (
                                    <div className="border-t border-stone-100 pt-3" data-testid="edit-user-form">
                                        <div className="bg-stone-50 rounded-lg p-4 space-y-3">
                                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Edit Details</h4>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div><label className="block text-xs font-medium text-stone-600 mb-1">Name</label>
                                                    <input value={editUserForm.name || ''} onChange={e => setEditUserForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" /></div>
                                                <div><label className="block text-xs font-medium text-stone-600 mb-1">Role</label>
                                                    <select value={editUserForm.role || 'sales_rep'} onChange={e => setEditUserForm(f => ({ ...f, role: e.target.value, is_admin: e.target.value === 'admin' }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500">
                                                        <option value="sales_rep">Sales Rep</option><option value="manager">Manager</option><option value="admin">Admin</option>
                                                    </select></div>
                                                <div><label className="block text-xs font-medium text-stone-600 mb-1">Org Role</label>
                                                    <select value={editUserForm.org_role || 'member'} onChange={e => setEditUserForm(f => ({ ...f, org_role: e.target.value }))} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500">
                                                        <option value="owner">Owner</option><option value="admin">Admin</option><option value="member">Member</option>
                                                    </select></div>
                                                <div className="flex items-end">
                                                    <label className="flex items-center gap-2 text-sm">
                                                        <input type="checkbox" checked={editUserForm.is_active} onChange={e => setEditUserForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded border-stone-300" />
                                                        Active
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 mt-4">
                                                <Button size="sm" onClick={handleUpdateUser}>Save</Button>
                                                <Button size="sm" variant="outline" onClick={() => setEditingUser(false)}>Cancel</Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-stone-100">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button variant="outline" size="sm" className="text-blue-600" onClick={() => handleImpersonate(selectedUser)} data-testid="impersonate-user-btn">
                                        <UserCog className="w-3.5 h-3.5 mr-1" /> Impersonate
                                    </Button>
                                    <Button variant="outline" size="sm" className="text-amber-600" onClick={() => { setResetPwUserId(selectedUser.user_id); setShowResetPw(true); }} data-testid="detail-reset-pw-btn">
                                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Set Password
                                    </Button>
                                    <Button variant="outline" size="sm" className="text-violet-600" onClick={() => sendResetEmail(selectedUser)} data-testid="detail-send-reset-email-btn">
                                        <Mail className="w-3.5 h-3.5 mr-1" /> Email Reset Link
                                    </Button>
                                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteUser(selectedUser.user_id, selectedUser.email)} data-testid="delete-user-btn">
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setEditingUser(!editingUser)} data-testid="edit-user-btn">
                                        <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setShowDetail(false)}>Close</Button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reset Password Modal */}
            <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>Set a new password for this user</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">New Password *</label>
                            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                placeholder="Min 8 characters" data-testid="reset-pw-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowResetPw(false); setNewPassword(''); }}>Cancel</Button>
                        <Button onClick={handleResetPassword} disabled={resetting} data-testid="reset-pw-submit">
                            {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
                            Reset Password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/* ─── Usage & Billing Tab ──────────────────────────────────────────────────── */
function UsageBillingTab({ getAuthHeader }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/super-admin/usage`, { headers: getAuthHeader() })
            .then(res => setData(res.data))
            .catch(() => toast.error('Failed to load usage data'))
            .finally(() => setLoading(false));
    }, [getAuthHeader]);

    if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-stone-600 animate-spin" /></div>;
    if (!data) return <div className="text-center py-16 text-stone-500">No data available</div>;

    const billing = data.billing || {};
    const planDist = data.plan_distribution || {};
    const orgUsage = data.org_usage || [];
    const totalOrgs = Object.values(planDist).reduce((a, b) => a + b, 0) || 1;

    const planBarColors = { free: '#94a3b8', starter: '#3b82f6', pro: '#6366f1', professional: '#6366f1', enterprise: '#f59e0b' };
    const planLabels = { free: 'Free', starter: 'Starter', pro: 'Pro', professional: 'Professional', enterprise: 'Enterprise' };

    return (
        <div className="space-y-6">
            {/* Billing KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="billing-kpis">
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-emerald-600" />
                        </div>
                        <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                            <ArrowUpRight className="w-3 h-3" /> Monthly
                        </span>
                    </div>
                    <div className="text-2xl font-bold text-stone-900" data-testid="total-mrr">${billing.total_mrr?.toLocaleString()}</div>
                    <div className="text-xs text-stone-500">Monthly Recurring Revenue</div>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-stone-700" />
                        </div>
                        <span className="flex items-center gap-1 text-xs font-medium text-stone-700 bg-stone-100 px-2 py-1 rounded-full">
                            <ArrowUpRight className="w-3 h-3" /> Annual
                        </span>
                    </div>
                    <div className="text-2xl font-bold text-stone-900" data-testid="total-arr">${billing.total_arr?.toLocaleString()}</div>
                    <div className="text-xs text-stone-500">Annual Recurring Revenue</div>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-stone-900">{totalOrgs}</div>
                    <div className="text-xs text-stone-500">Total Organizations</div>
                </div>
            </div>

            {/* Plan Distribution + Revenue Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="plan-distribution">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4">Plan Distribution</h3>
                    <div className="space-y-3">
                        {Object.entries(planDist).map(([plan, count]) => (
                            <div key={plan}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-stone-700 capitalize">{planLabels[plan] || plan}</span>
                                    <span className="text-sm font-medium text-stone-900">{count} orgs</span>
                                </div>
                                <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{
                                        width: `${(count / totalOrgs) * 100}%`,
                                        backgroundColor: planBarColors[plan] || '#94a3b8',
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="revenue-breakdown">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4">Revenue by Plan</h3>
                    <div className="space-y-3">
                        {Object.entries(billing.revenue_by_plan || {}).map(([plan, info]) => (
                            <div key={plan} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: planBarColors[plan] || '#94a3b8' }} />
                                    <span className="text-sm text-stone-700 capitalize">{planLabels[plan] || plan}</span>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium text-stone-900">${info.mrr}/mo</div>
                                    <div className="text-xs text-stone-400">{info.count} orgs x ${info.price}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Growth Charts */}
            {(data.org_growth?.length > 0 || data.user_growth?.length > 0) && (
                <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="growth-charts">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4">Growth Trends (Last 12 Months)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <GrowthBars data={data.org_growth || []} label="New Organizations" color="#6366f1" />
                        <GrowthBars data={data.user_growth || []} label="New Users" color="#3b82f6" />
                    </div>
                </div>
            )}

            {/* Per-Org Usage Table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="org-usage-table">
                <div className="px-5 py-3 border-b border-stone-100">
                    <h3 className="text-sm font-semibold text-stone-900">Organization Usage Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-stone-50 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                                <th className="px-4 py-3">Organization</th>
                                <th className="px-4 py-3">Plan</th>
                                <th className="px-4 py-3 text-center">Users</th>
                                <th className="px-4 py-3 text-center">Contacts</th>
                                <th className="px-4 py-3 text-center">Deals</th>
                                <th className="px-4 py-3 text-center">Companies</th>
                                <th className="px-4 py-3 text-center">Sequences</th>
                                <th className="px-4 py-3 text-center">Activities</th>
                                <th className="px-4 py-3">Capacity</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {orgUsage.map(org => (
                                <tr key={org.org_id} className="hover:bg-stone-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-stone-900">{org.name}</div>
                                        <span className={`inline-flex px-1.5 py-0.5 text-xs rounded-full ${statusColors[org.status] || 'bg-stone-100 text-stone-600'}`}>{org.status}</span>
                                    </td>
                                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${planColors[org.plan] || planColors.free}`}>{org.plan}</span></td>
                                    <td className="px-4 py-3 text-center text-sm">{org.users}/{org.max_users}</td>
                                    <td className="px-4 py-3 text-center text-sm">{org.contacts?.toLocaleString()}/{org.max_contacts?.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-center text-sm">{org.deals}</td>
                                    <td className="px-4 py-3 text-center text-sm">{org.accounts}</td>
                                    <td className="px-4 py-3 text-center text-sm">{org.sequences}</td>
                                    <td className="px-4 py-3 text-center text-sm">{org.activities}</td>
                                    <td className="px-4 py-3">
                                        <div className="space-y-1">
                                            <CapacityBar label="Users" pct={org.users_pct} />
                                            <CapacityBar label="Contacts" pct={org.contacts_pct} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* ─── Helper Components ────────────────────────────────────────────────────── */
function CapacityBar({ label, pct }) {
    const clampedPct = Math.min(pct, 100);
    const color = clampedPct > 90 ? '#ef4444' : clampedPct > 70 ? '#f59e0b' : '#10b981';
    return (
        <div className="flex items-center gap-2">
            <span className="text-xs text-stone-400 w-14">{label}</span>
            <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden w-16">
                <div className="h-full rounded-full transition-all" style={{ width: `${clampedPct}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs text-stone-500 w-8 text-right">{pct}%</span>
        </div>
    );
}

function GrowthBars({ data, label, color }) {
    if (!data.length) return null;
    const maxVal = Math.max(...data.map(d => d.count), 1);
    return (
        <div>
            <div className="text-xs font-medium text-stone-500 mb-2">{label}</div>
            <div className="flex items-end gap-1 h-24">
                {data.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full rounded-t transition-all" style={{
                            height: `${(d.count / maxVal) * 100}%`,
                            backgroundColor: color,
                            minHeight: d.count > 0 ? '4px' : '0px',
                        }} />
                        <span className="text-[9px] text-stone-400 leading-none">{d.month?.slice(5)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ── AI Usage Tab ──────────────────────────────────────────────────────── */
function AIUsageTab({ getAuthHeader }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/super-admin/ai-usage?days=${days}`, { headers: getAuthHeader() });
            setData(res.data);
        } catch { toast.error('Failed to load AI usage data'); }
        finally { setLoading(false); }
    }, [getAuthHeader, days]);

    useEffect(() => { loadData(); }, [loadData]);

    const fmt = (n) => typeof n === 'number' ? n.toLocaleString() : '0';
    const fmtCost = (n) => typeof n === 'number' ? `$${n.toFixed(4)}` : '$0.00';
    const fmtCostShort = (n) => typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00';

    const featureLabel = (f) => (f || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    if (!data) return <div className="text-center text-stone-500 py-20">No AI usage data available.</div>;

    const { summary, by_org, by_user, by_feature, daily_trend } = data;
    const avgCostPerCall = summary.total_calls > 0 ? summary.total_cost_usd / summary.total_calls : 0;
    const maxDailyTokens = Math.max(...(daily_trend || []).map(d => d.tokens), 1);

    return (
        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500">Period:</span>
                {[7, 30, 90].map(d => (
                    <button
                        key={d}
                        onClick={() => setDays(d)}
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                            days === d
                                ? 'bg-stone-800 text-white'
                                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                    >
                        {d} days
                    </button>
                ))}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total API Calls', value: fmt(summary.total_calls), icon: Zap, color: 'text-blue-600 bg-blue-50' },
                    { label: 'Total Tokens', value: fmt(summary.total_tokens), icon: Activity, color: 'text-purple-600 bg-purple-50' },
                    { label: 'Total Cost', value: fmtCostShort(summary.total_cost_usd), icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
                    { label: 'Avg Cost / Call', value: fmtCost(avgCostPerCall), icon: TrendingUp, color: 'text-amber-600 bg-amber-50' },
                ].map(card => (
                    <div key={card.label} className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${card.color}`}>
                            <card.icon className="w-4.5 h-4.5" />
                        </div>
                        <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{card.value}</div>
                        <div className="text-xs text-stone-500 mt-0.5">{card.label}</div>
                    </div>
                ))}
            </div>

            {/* Daily Trend */}
            {daily_trend && daily_trend.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-700 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Daily Usage Trend</h3>
                    <div className="flex items-end gap-[2px] h-32">
                        {daily_trend.map((d, i) => (
                            <div
                                key={d.date}
                                className="group relative flex-1 min-w-[3px]"
                                style={{ height: '100%' }}
                            >
                                <div
                                    className="absolute bottom-0 w-full bg-stone-700 rounded-t-sm hover:bg-stone-900 transition-colors"
                                    style={{ height: `${Math.max((d.tokens / maxDailyTokens) * 100, 2)}%` }}
                                />
                                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                                    <div className="bg-stone-800 text-white text-[10px] rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                                        <div className="font-medium">{d.date}</div>
                                        <div>{fmt(d.tokens)} tokens</div>
                                        <div>{fmt(d.calls)} calls</div>
                                        <div>{fmtCost(d.cost_usd)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-2 text-[10px] text-stone-400">
                        <span>{daily_trend[0]?.date}</span>
                        <span>{daily_trend[daily_trend.length - 1]?.date}</span>
                    </div>
                </div>
            )}

            {/* By Feature */}
            {by_feature && by_feature.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Usage by Feature</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Feature</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Calls</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Tokens</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Cost</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs w-40">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {by_feature.map(f => (
                                    <tr key={f.feature} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-2 px-3 font-medium text-stone-800">{featureLabel(f.feature)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(f.call_count)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(f.total_tokens)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmtCost(f.total_cost_usd)}</td>
                                        <td className="py-2 px-3">
                                            <div className="w-full bg-stone-100 rounded-full h-2">
                                                <div
                                                    className="bg-stone-700 h-2 rounded-full"
                                                    style={{ width: `${summary.total_tokens > 0 ? (f.total_tokens / summary.total_tokens) * 100 : 0}%` }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* By Organization */}
            {by_org && by_org.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Usage by Organization</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Organization</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Calls</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Tokens</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Cost</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Top Feature</th>
                                </tr>
                            </thead>
                            <tbody>
                                {by_org.map(org => {
                                    const topFeature = org.features
                                        ? Object.entries(org.features).sort((a, b) => b[1] - a[1])[0]
                                        : null;
                                    return (
                                        <tr key={org.org_id} className="border-b border-stone-50 hover:bg-stone-50">
                                            <td className="py-2 px-3 font-medium text-stone-800">{org.org_name}</td>
                                            <td className="py-2 px-3 text-right text-stone-600">{fmt(org.call_count)}</td>
                                            <td className="py-2 px-3 text-right text-stone-600">{fmt(org.total_tokens)}</td>
                                            <td className="py-2 px-3 text-right text-stone-600">{fmtCost(org.total_cost_usd)}</td>
                                            <td className="py-2 px-3">
                                                {topFeature ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-xs">
                                                        {featureLabel(topFeature[0])}
                                                        <span className="text-stone-400">({topFeature[1]})</span>
                                                    </span>
                                                ) : <span className="text-stone-400">-</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* By User */}
            {by_user && by_user.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Usage by User</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">User</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Email</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Organization</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Calls</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Tokens</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {by_user.map(u => (
                                    <tr key={u.user_id} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-2 px-3 font-medium text-stone-800">{u.user_name}</td>
                                        <td className="py-2 px-3 text-stone-500 text-xs">{u.user_email}</td>
                                        <td className="py-2 px-3 text-stone-600">{u.org_name}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(u.call_count)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(u.total_tokens)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmtCost(u.total_cost_usd)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}


/* ── Service Usage Tab (Apify + Exa cross-org) ────────────────────────── */
function ServiceUsageTab({ getAuthHeader }) {
    const [apifyData, setApifyData] = useState(null);
    const [exaData, setExaData] = useState(null);
    const [rekData, setRekData] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [apifyRes, exaRes, rekRes, summaryRes] = await Promise.all([
                axios.get(`${API}/usage/admin/apify?period=${period}`, { headers: getAuthHeader() }).catch(() => null),
                axios.get(`${API}/usage/admin/exa?period=${period}`, { headers: getAuthHeader() }).catch(() => null),
                axios.get(`${API}/usage/admin/rekognition?period=${period}`, { headers: getAuthHeader() }).catch(() => null),
                axios.get(`${API}/usage/admin/services?period=${period}`, { headers: getAuthHeader() }).catch(() => null),
            ]);
            if (apifyRes) setApifyData(apifyRes.data);
            if (exaRes) setExaData(exaRes.data);
            if (rekRes) setRekData(rekRes.data);
            if (summaryRes) setSummary(summaryRes.data);
        } catch { toast.error('Failed to load service usage data'); }
        finally { setLoading(false); }
    }, [getAuthHeader, period]);

    useEffect(() => { loadData(); }, [loadData]);

    const fmt = (n) => typeof n === 'number' ? n.toLocaleString() : '0';
    const fmtCost = (n) => typeof n === 'number' ? `$${n.toFixed(4)}` : '$0.00';
    const fmtCostShort = (n) => typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00';

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500">Period:</span>
                {['7d', '30d', '90d'].map(p => (
                    <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                            period === p
                                ? 'bg-stone-800 text-white'
                                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                    >
                        {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                    </button>
                ))}
            </div>

            {/* Combined Summary Cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 text-stone-600 bg-stone-50">
                            <DollarSign className="w-4.5 h-4.5" />
                        </div>
                        <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtCostShort(summary.total_cost)}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Total Service Cost</div>
                    </div>
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 text-purple-600 bg-purple-50">
                            <Brain className="w-4.5 h-4.5" />
                        </div>
                        <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtCostShort(summary.services?.ai?.cost)}</div>
                        <div className="text-xs text-stone-500 mt-0.5">AI (OpenAI)</div>
                    </div>
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 text-amber-600 bg-amber-50">
                            <Database className="w-4.5 h-4.5" />
                        </div>
                        <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtCostShort(summary.services?.apify?.cost)}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Apify (Enrichment)</div>
                    </div>
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 text-blue-600 bg-blue-50">
                            <Zap className="w-4.5 h-4.5" />
                        </div>
                        <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtCostShort(summary.services?.exa?.cost)}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Exa (Web Search)</div>
                    </div>
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 text-emerald-600 bg-emerald-50">
                            <Eye className="w-4.5 h-4.5" />
                        </div>
                        <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtCostShort(summary.services?.rekognition?.cost)}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Rekognition (Faces)</div>
                    </div>
                </div>
            )}

            {/* Apify Cross-Org */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Apify Enrichment — By Organization</h3>
                {apifyData && apifyData.by_org?.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Organization</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Runs</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Cost</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs w-40">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apifyData.by_org.map(org => (
                                    <tr key={org.org_id} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-2 px-3 font-medium text-stone-800">{org.org_name}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(org.runs)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmtCost(org.cost)}</td>
                                        <td className="py-2 px-3">
                                            <div className="w-full bg-stone-100 rounded-full h-2">
                                                <div
                                                    className="bg-amber-500 h-2 rounded-full"
                                                    style={{ width: `${apifyData.total_runs > 0 ? (org.runs / apifyData.total_runs) * 100 : 0}%` }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-stone-400 text-sm">No Apify usage data for this period</div>
                )}
            </div>

            {/* Exa Cross-Org */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Exa Web Search — By Organization</h3>
                {exaData && exaData.by_org?.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Organization</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Searches</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Cost</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs w-40">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {exaData.by_org.map(org => (
                                    <tr key={org.org_id} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-2 px-3 font-medium text-stone-800">{org.org_name}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(org.runs)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmtCost(org.cost)}</td>
                                        <td className="py-2 px-3">
                                            <div className="w-full bg-stone-100 rounded-full h-2">
                                                <div
                                                    className="bg-blue-500 h-2 rounded-full"
                                                    style={{ width: `${exaData.total_runs > 0 ? (org.runs / exaData.total_runs) * 100 : 0}%` }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-stone-400 text-sm">No Exa search usage data for this period</div>
                )}
            </div>

            {/* Rekognition Cross-Org */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Rekognition Face Matching — By Organization</h3>
                {rekData && rekData.by_org?.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs">Organization</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">API Calls</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Images</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium text-xs">Cost</th>
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium text-xs w-40">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rekData.by_org.map(org => (
                                    <tr key={org.org_id} className="border-b border-stone-50 hover:bg-stone-50">
                                        <td className="py-2 px-3 font-medium text-stone-800">{org.org_name}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(org.calls)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmt(org.images)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">{fmtCost(org.cost)}</td>
                                        <td className="py-2 px-3">
                                            <div className="w-full bg-stone-100 rounded-full h-2">
                                                <div
                                                    className="bg-emerald-500 h-2 rounded-full"
                                                    style={{ width: `${rekData.total_calls > 0 ? (org.calls / rekData.total_calls) * 100 : 0}%` }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-stone-400 text-sm">No Rekognition usage data for this period</div>
                )}
            </div>
        </div>
    );
}


/* ── Platform Admins Tab ─────────────────────────────────────────────────
 *
 * Manage users with is_super_admin=True. Backed by:
 *   GET    /api/super-admin/platform-admins
 *   POST   /api/super-admin/platform-admins  (create OR promote existing)
 *   DELETE /api/super-admin/platform-admins/{user_id}  (demote)
 *
 * Self-demote is blocked server-side. Demoting the last admin is also
 * blocked server-side. Frontend hides the Demote button on the current
 * user's row as a UX cue (rather than a security boundary).
 */
function PlatformAdminsTab({ getAuthHeader, currentUser }) {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ email: '', name: '', password: '' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/super-admin/platform-admins`, { headers: getAuthHeader() });
            setAdmins(res.data?.platform_admins || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load Platform Admins');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        const email = (form.email || '').trim().toLowerCase();
        if (!email || !email.includes('@')) {
            toast.error('Valid email required');
            return;
        }
        setAdding(true);
        try {
            const res = await axios.post(
                `${API}/super-admin/platform-admins`,
                { email, name: form.name || '', password: form.password || undefined },
                { headers: getAuthHeader() }
            );
            const action = res.data?.action;
            toast.success(
                action === 'promoted'
                    ? `${email} promoted to Platform Admin`
                    : `${email} created as Platform Admin`
            );
            setShowAdd(false);
            setForm({ email: '', name: '', password: '' });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to add Platform Admin');
        } finally {
            setAdding(false);
        }
    };

    const handleDemote = async (admin) => {
        const ok = window.confirm(
            `Demote ${admin.email} from Platform Admin?\n\n` +
            `Their user record stays — only the is_super_admin flag is removed.\n` +
            `This action is audit-logged.`
        );
        if (!ok) return;
        try {
            await axios.delete(`${API}/super-admin/platform-admins/${admin.user_id}`, { headers: getAuthHeader() });
            toast.success(`${admin.email} is no longer a Platform Admin`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to demote');
        }
    };

    // Send a 24h password-reset link to a Platform Admin (including
    // yourself — useful if you forgot your password and have another
    // admin available to trigger the email). Same backend endpoint as
    // the Users tab; reuses Resend + token generation.
    const handleSendResetEmail = async (admin) => {
        const ok = window.confirm(
            `Send a password-reset email to ${admin.email}?\n\n` +
            `They'll get a link (valid 24h) to set their own password. ` +
            `This is the recommended option vs. handing them a password directly.`
        );
        if (!ok) return;
        try {
            const res = await axios.post(
                `${API}/super-admin/users/${admin.user_id}/send-reset-email`,
                {},
                { headers: getAuthHeader() }
            );
            if (res.data?.delivered === false) {
                toast.warning(`Token created but email failed — check logs. ${admin.email} can still use the next reset.`);
            } else {
                toast.success(`Reset email sent to ${admin.email}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send reset email');
        }
    };

    return (
        <div className="space-y-4" data-testid="platform-admins-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-stone-900">Platform Admins</h2>
                    <p className="text-sm text-stone-500 mt-1">
                        Users with full platform access (is_super_admin). Promote any existing user, or
                        create a new admin account. Every change is audit-logged.
                    </p>
                </div>
                <Button onClick={() => setShowAdd(true)}>
                    <UserPlus className="w-4 h-4 mr-2" /> Add Platform Admin
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12 text-stone-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : admins.length === 0 ? (
                <div className="text-center py-12 text-stone-500 text-sm border border-dashed border-stone-200 rounded-lg">
                    No Platform Admins. (You should at least see yourself here — if not, refresh.)
                </div>
            ) : (
                <div className="border border-stone-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium text-stone-700">Name</th>
                                <th className="text-left px-4 py-3 font-medium text-stone-700">Email</th>
                                <th className="text-left px-4 py-3 font-medium text-stone-700">Created</th>
                                <th className="text-left px-4 py-3 font-medium text-stone-700">Status</th>
                                <th className="text-right px-4 py-3 font-medium text-stone-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {admins.map(a => {
                                const isMe = currentUser?.user_id === a.user_id;
                                return (
                                    <tr key={a.user_id} className="hover:bg-stone-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Shield className="w-4 h-4 text-violet-500" />
                                                <span className="font-medium text-stone-900">
                                                    {a.name || '—'}
                                                </span>
                                                {isMe && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold uppercase tracking-wide">
                                                        You
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-stone-600">{a.email}</td>
                                        <td className="px-4 py-3 text-stone-500 text-xs">
                                            {a.created_at ? String(a.created_at).slice(0, 10) : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                a.is_active === false
                                                    ? 'bg-stone-100 text-stone-500'
                                                    : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                {a.is_active === false ? 'Inactive' : 'Active'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    onClick={() => handleSendResetEmail(a)}
                                                    className="text-xs px-2 py-1 rounded text-violet-600 hover:bg-violet-50 inline-flex items-center"
                                                    data-testid={`send-reset-email-admin-${a.user_id}`}
                                                    title="Send password reset email"
                                                >
                                                    <Mail className="w-3.5 h-3.5 inline mr-1" />
                                                    Email Reset
                                                </button>
                                                {isMe ? (
                                                    <span className="text-xs text-stone-400 italic ml-2">
                                                        Can't demote yourself
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleDemote(a)}
                                                        className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50 inline-flex items-center"
                                                        data-testid={`demote-${a.user_id}`}
                                                    >
                                                        <ShieldOff className="w-3.5 h-3.5 inline mr-1" />
                                                        Demote
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add Platform Admin Dialog */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Platform Admin</DialogTitle>
                        <DialogDescription>
                            Promote an existing user (just enter their email) or create a new admin account
                            (enter email + password + name). Promotions don't change the user's password
                            unless you provide one. New accounts have no org binding.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-stone-700 mb-1 block">Email *</label>
                            <input
                                value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="someone@example.com"
                                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                                data-testid="admin-email-input"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-stone-700 mb-1 block">Name (new accounts only)</label>
                            <input
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="First Last"
                                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-stone-700 mb-1 block">
                                Password (required for new accounts; optional for promotions)
                            </label>
                            <input
                                type="password"
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                placeholder="Min 8 characters"
                                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                                data-testid="admin-password-input"
                            />
                            <p className="text-[10px] text-stone-500 mt-1">
                                Providing a password on a promotion will reset the existing user's password.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAdd(false)} disabled={adding}>
                            Cancel
                        </Button>
                        <Button onClick={handleAdd} disabled={adding || !form.email}>
                            {adding ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</>) : 'Add Platform Admin'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


/* ── Audit Logs Tab ─────────────────────────────────────────────────────── */
function AuditLogsTab({ getAuthHeader }) {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterAction, setFilterAction] = useState('');
    const [filterActor, setFilterActor] = useState('');
    const [page, setPage] = useState(0);
    const limit = 30;

    useEffect(() => { loadLogs(); }, [filterAction, filterActor, page]);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterAction) params.set('action', filterAction);
            if (filterActor) params.set('actor_email', filterActor);
            params.set('limit', limit);
            params.set('offset', page * limit);
            const res = await axios.get(`${API}/super-admin/audit-logs?${params}`, { headers: getAuthHeader() });
            setLogs(res.data.logs || []);
            setTotal(res.data.total || 0);
            if (res.data.actions) setActions(res.data.actions);
        } catch { toast.error('Failed to load audit logs'); }
        finally { setLoading(false); }
    };

    const actionLabel = (action) => {
        const labels = {
            create_organization: 'Created Org', suspend_organization: 'Suspended Org',
            activate_organization: 'Activated Org', delete_organization: 'Deleted Org',
            create_user: 'Created User', delete_user: 'Deleted User',
            impersonate_user: 'Impersonated User',
        };
        return labels[action] || action.replace(/_/g, ' ');
    };

    const actionColor = (action) => {
        if (action.includes('delete')) return 'bg-red-100 text-red-700';
        if (action.includes('suspend')) return 'bg-amber-100 text-amber-700';
        if (action.includes('impersonate')) return 'bg-blue-100 text-blue-700';
        if (action.includes('create')) return 'bg-emerald-100 text-emerald-700';
        if (action.includes('activate')) return 'bg-emerald-100 text-emerald-700';
        return 'bg-stone-100 text-stone-700';
    };

    return (
        <div data-testid="audit-logs-tab">
            {/* Filters */}
            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input type="text" placeholder="Filter by actor email..." value={filterActor}
                        onChange={e => { setFilterActor(e.target.value); setPage(0); }}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        data-testid="audit-filter-actor" />
                </div>
                <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }}
                    className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none"
                    data-testid="audit-filter-action">
                    <option value="">All Actions</option>
                    {actions.map(a => <option key={a} value={a}>{actionLabel(a)}</option>)}
                </select>
                <span className="text-sm text-stone-400">{total} entries</span>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
            ) : logs.length === 0 ? (
                <div className="text-center py-16 text-stone-400">
                    <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit logs found</p>
                </div>
            ) : (
                <>
                    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100 bg-stone-50">
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Time</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Actor</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Action</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Target</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.log_id} className="border-b border-stone-50 hover:bg-stone-50/50" data-testid={`audit-row-${log.log_id}`}>
                                        <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">
                                            {new Date(log.created_at).toLocaleDateString()} {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="px-4 py-3 text-stone-700 font-medium">{log.actor_email}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(log.action)}`}>
                                                {actionLabel(log.action)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-stone-600 text-xs">
                                            <span className="text-stone-400">{log.target_type}</span>
                                            {log.target_id && <span className="ml-1 font-mono text-stone-500">{log.target_id.slice(0, 8)}...</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-stone-500 max-w-xs truncate">
                                            {Object.entries(log.details || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {total > limit && (
                        <div className="flex items-center justify-between mt-4">
                            <span className="text-sm text-stone-500">Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}</span>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                                <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


/* ─── Data Management Tab ──────────────────────────────────────────────── */
function DataManagementTab({ getAuthHeader, onRefresh }) {
    const [purging, setPurging] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [scope, setScope] = useState('all');
    const [result, setResult] = useState(null);

    const handlePurge = async () => {
        if (confirmText !== 'PURGE') return toast.error('Type PURGE to confirm');
        setPurging(true);
        setResult(null);
        try {
            const res = await axios.post(`${API}/super-admin/purge-data`, { scope, confirm: 'PURGE' }, { headers: getAuthHeader() });
            setResult(res.data);
            toast.success(res.data.message);
            setConfirmText('');
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Purge failed');
        } finally {
            setPurging(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="data-management-tab">
            <div className="rounded-xl border-2 border-red-200 bg-red-50/50 p-6">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-red-900">Purge Database</h3>
                        <p className="text-sm text-red-700 mt-1 mb-4">
                            Permanently delete all CRM data from the platform. This action cannot be undone. Super admin accounts will be preserved.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-red-800 uppercase mb-1.5">Purge Scope</label>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setScope('all')}
                                        className={`px-4 py-2 text-sm rounded-lg border font-medium transition-colors ${scope === 'all' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
                                        data-testid="scope-all"
                                    >
                                        Everything (users, orgs & data)
                                    </button>
                                    <button
                                        onClick={() => setScope('org_data_only')}
                                        className={`px-4 py-2 text-sm rounded-lg border font-medium transition-colors ${scope === 'org_data_only' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
                                        data-testid="scope-org-data"
                                    >
                                        CRM Data Only (keep users & orgs)
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-red-800 uppercase mb-1.5">
                                    Type PURGE to confirm
                                </label>
                                <div className="flex gap-3">
                                    <input
                                        type="text"
                                        value={confirmText}
                                        onChange={e => setConfirmText(e.target.value)}
                                        placeholder="Type PURGE here"
                                        className="w-48 px-3 py-2 text-sm border border-red-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400 font-mono"
                                        data-testid="purge-confirm-input"
                                    />
                                    <Button
                                        onClick={handlePurge}
                                        disabled={purging || confirmText !== 'PURGE'}
                                        className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                        data-testid="purge-btn"
                                    >
                                        {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                        {purging ? 'Purging...' : 'Purge Data'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {result && (
                <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="purge-result">
                    <h4 className="font-semibold text-stone-900 mb-3">Purge Complete</h4>
                    <p className="text-sm text-stone-600 mb-3">{result.message}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {Object.entries(result.deleted || {}).map(([collection, count]) => (
                            <div key={collection} className="px-3 py-2 bg-stone-50 rounded-lg">
                                <div className="text-lg font-bold text-stone-900">{count}</div>
                                <div className="text-xs text-stone-500 capitalize">{collection.replace(/_/g, ' ')}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


/* ─── Feature Requests Admin Tab ───────────────────────────────────────── */
const FR_STATUSES = ['submitted', 'selected', 'planning', 'in_progress', 'released'];
const FR_STATUS_LABELS = {
    submitted: { label: 'Submitted', color: 'bg-stone-100 text-stone-600', headerBg: 'bg-stone-200', headerText: 'text-stone-700', border: 'border-stone-300' },
    selected: { label: 'Selected', color: 'bg-blue-100 text-blue-700', headerBg: 'bg-blue-100', headerText: 'text-blue-800', border: 'border-blue-300' },
    planning: { label: 'Planning', color: 'bg-amber-100 text-amber-700', headerBg: 'bg-amber-100', headerText: 'text-amber-800', border: 'border-amber-300' },
    in_progress: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-700', headerBg: 'bg-indigo-100', headerText: 'text-indigo-800', border: 'border-indigo-300' },
    released: { label: 'Released', color: 'bg-emerald-100 text-emerald-700', headerBg: 'bg-emerald-100', headerText: 'text-emerald-800', border: 'border-emerald-300' },
};
const FR_TYPES = ['bug', 'feature'];
const FR_PRIORITIES = ['low', 'medium', 'high', 'critical'];
const FR_CATEGORIES = ['ui', 'backend', 'performance', 'integration', 'ai', 'security', 'other'];
const FR_PRIORITY_COLORS = {
    low: 'bg-stone-100 text-stone-600',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
};

function FeatureRequestsAdminTab({ getAuthHeader }) {
    const [requests, setRequests] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ type: '', priority: '', category: '', search: '' });
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkStatus, setBulkStatus] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settings, setSettings] = useState(null);
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminNotes, setAdminNotes] = useState({});
    const [savingNotes, setSavingNotes] = useState(null);
    // v2.16.36 Magic Dispatch state — modal holds the request_id currently
    // being dispatched; `extraContext` is Matt's free-text guidance that
    // gets embedded in the Claude Code prompt.
    const [dispatchingId, setDispatchingId] = useState(null);
    const [extraContext, setExtraContext] = useState('');
    const [isDispatching, setIsDispatching] = useState(false);
    // v2.16.39 BUG-031: admin /all endpoint excludes attachments (projection
    // skips the heavy base64 data_url to keep the list light). Lazy-load the
    // full issue doc — WITH attachments — when the user expands a card, and
    // cache by request_id so re-expanding doesn't refetch.
    const [attachmentsCache, setAttachmentsCache] = useState({});
    const [loadingAttachments, setLoadingAttachments] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.category) params.append('category', filters.category);
            const qs = params.toString() ? `?${params.toString()}` : '';
            const [reqRes, statsRes] = await Promise.all([
                axios.get(`${API}/feature-requests/admin/all${qs}`, { headers: getAuthHeader() }),
                axios.get(`${API}/feature-requests/admin/stats`, { headers: getAuthHeader() }),
            ]);
            const requestArr = Array.isArray(reqRes.data) ? reqRes.data : (reqRes.data?.data || []);
            setRequests(requestArr);
            setStats(statsRes.data);
            // Initialize admin notes from loaded data
            const notesMap = {};
            requestArr.forEach(r => { if (r.admin_notes) notesMap[r.request_id] = r.admin_notes; });
            setAdminNotes(prev => ({ ...prev, ...notesMap }));
        } catch (err) {
            toast.error('Failed to load feature requests');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, filters.type, filters.priority, filters.category]);

    useEffect(() => { load(); }, [load]);

    // v2.16.39 BUG-031: when a card expands, lazily fetch its attachments.
    useEffect(() => {
        if (!selectedCard) return;
        if (attachmentsCache[selectedCard] !== undefined) return;  // already cached (including empty-array result)
        const req = requests.find(r => r.request_id === selectedCard);
        if (!req || !req.attachment_count) {
            // No attachments expected — cache an empty array so we don't re-check.
            setAttachmentsCache(prev => ({ ...prev, [selectedCard]: [] }));
            return;
        }
        setLoadingAttachments(selectedCard);
        axios.get(`${API}/feature-requests/${selectedCard}`, { headers: getAuthHeader() })
            .then(res => {
                setAttachmentsCache(prev => ({ ...prev, [selectedCard]: res.data?.attachments || [] }));
            })
            .catch(() => {
                setAttachmentsCache(prev => ({ ...prev, [selectedCard]: [] }));
                toast.error('Failed to load screenshots');
            })
            .finally(() => setLoadingAttachments(null));
    }, [selectedCard, attachmentsCache, requests, getAuthHeader]);

    // Load admin users for assignment
    useEffect(() => {
        const loadAdminUsers = async () => {
            try {
                const res = await axios.get(`${API}/super-admin/users`, { headers: getAuthHeader() });
                const users = Array.isArray(res.data) ? res.data : res.data.users || [];
                setAdminUsers(users.filter(u => u.is_super_admin));
            } catch { /* ignore */ }
        };
        loadAdminUsers();
    }, [getAuthHeader]);

    const changeStatus = async (requestId, newStatus) => {
        try {
            await axios.put(`${API}/feature-requests/${requestId}/status`, { status: newStatus }, { headers: getAuthHeader() });
            toast.success(`Status updated to ${FR_STATUS_LABELS[newStatus]?.label || newStatus}`);
            if (newStatus === 'released') {
                toast.info('Users have been notified via email and in-app notifications');
            }
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update status');
        }
    };

    const changePriority = async (requestId, priority) => {
        try {
            await axios.put(`${API}/feature-requests/${requestId}/priority`, { priority }, { headers: getAuthHeader() });
            toast.success('Priority updated');
            load();
        } catch { toast.error('Failed to update priority'); }
    };

    const changeCategory = async (requestId, category) => {
        try {
            await axios.put(`${API}/feature-requests/${requestId}/category`, { category }, { headers: getAuthHeader() });
            toast.success('Category updated');
            load();
        } catch { toast.error('Failed to update category'); }
    };

    const assignTo = async (requestId, userId, userName) => {
        try {
            await axios.put(`${API}/feature-requests/${requestId}/assign`, { assigned_to: userId, assigned_to_name: userName }, { headers: getAuthHeader() });
            toast.success(`Assigned to ${userName || 'nobody'}`);
            load();
        } catch { toast.error('Failed to assign'); }
    };

    const saveAdminNotes = async (requestId) => {
        setSavingNotes(requestId);
        try {
            await axios.put(`${API}/feature-requests/${requestId}/admin-notes`, { admin_notes: adminNotes[requestId] || '' }, { headers: getAuthHeader() });
            toast.success('Notes saved');
        } catch { toast.error('Failed to save notes'); }
        finally { setSavingNotes(null); }
    };

    const deleteRequest = async (requestId) => {
        if (!window.confirm('Delete this feature request? This cannot be undone.')) return;
        try {
            await axios.delete(`${API}/feature-requests/${requestId}`, { headers: getAuthHeader() });
            toast.success('Feature request deleted');
            setSelectedCard(null);
            load();
        } catch { toast.error('Failed to delete'); }
    };

    // v2.16.36 Magic Dispatch — open the dispatch modal for a given request
    const openDispatchModal = (req) => {
        setDispatchingId(req.request_id);
        setExtraContext('');
    };

    // Fires the cloud Claude Code Routine. On 409 (5-min guard), asks for
    // confirmation and retries with force=true. Other errors surface via toast.
    const dispatchToClaudeCode = async (force = false) => {
        if (!dispatchingId) return;
        setIsDispatching(true);
        try {
            const res = await axios.post(
                `${API}/feature-requests/${dispatchingId}/dispatch`,
                { extra_context: extraContext, force },
                { headers: getAuthHeader() }
            );
            toast.success('Dispatched to Claude Code');
            if (res.data.live_url) {
                window.open(res.data.live_url, '_blank', 'noopener,noreferrer');
            }
            setDispatchingId(null);
            setExtraContext('');
            load();
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 409 && detail) {
                if (window.confirm(`${detail}\n\nDispatch again anyway?`)) {
                    setIsDispatching(false);
                    return dispatchToClaudeCode(true);
                }
            } else if (status === 503) {
                toast.error('Claude Code Routine not configured. Ask Matt to add SSM params.');
            } else {
                toast.error(detail || 'Dispatch failed');
            }
        } finally {
            setIsDispatching(false);
        }
    };

    const bulkChangeStatus = async () => {
        if (!bulkStatus || selectedIds.size === 0) return;
        try {
            await axios.put(`${API}/feature-requests/admin/bulk-status`, { request_ids: Array.from(selectedIds), status: bulkStatus }, { headers: getAuthHeader() });
            toast.success(`Updated ${selectedIds.size} items to ${FR_STATUS_LABELS[bulkStatus]?.label}`);
            setSelectedIds(new Set());
            setBulkStatus('');
            load();
        } catch { toast.error('Bulk update failed'); }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const loadSettings = async () => {
        try {
            const res = await axios.get(`${API}/feature-requests/admin/settings`, { headers: getAuthHeader() });
            setSettings(res.data);
        } catch { setSettings({ notification_emails: [], notify_on_new_submission: true, notify_on_critical_bugs: true }); }
    };

    const saveSettings = async () => {
        try {
            await axios.put(`${API}/feature-requests/admin/settings`, settings, { headers: getAuthHeader() });
            toast.success('Settings saved');
            setSettingsOpen(false);
        } catch { toast.error('Failed to save settings'); }
    };

    // Filter requests by search term (client-side)
    const filteredRequests = requests.filter(r => {
        if (!filters.search) return true;
        const q = filters.search.toLowerCase();
        return r.title?.toLowerCase().includes(q) || r.ticket_number?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q);
    });

    // Group by status for Kanban columns
    const columns = FR_STATUSES.map(status => ({
        status,
        ...FR_STATUS_LABELS[status],
        items: filteredRequests.filter(r => r.status === status),
    }));

    return (
        <div className="space-y-5" data-testid="feature-requests-admin-tab">
            {/* Stats Bar */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                    <div className="p-3 rounded-xl bg-stone-50 border border-stone-200 text-center">
                        <div className="text-2xl font-bold font-[Manrope] text-stone-900">{stats.total}</div>
                        <div className="text-xs text-stone-500">Total</div>
                    </div>
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-center">
                        <div className="text-2xl font-bold font-[Manrope] text-red-700">{stats.open_bugs || 0}</div>
                        <div className="text-xs text-red-500">Open Bugs</div>
                    </div>
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-center">
                        <div className="text-2xl font-bold font-[Manrope] text-red-700">{stats.critical_count || 0}</div>
                        <div className="text-xs text-red-500">Critical</div>
                    </div>
                    {FR_STATUSES.map(s => (
                        <div key={s} className="p-3 rounded-xl bg-stone-50 border border-stone-200 text-center">
                            <div className="text-2xl font-bold font-[Manrope] text-stone-900">{stats.by_status?.[s]?.count || 0}</div>
                            <div className="text-xs text-stone-500">{FR_STATUS_LABELS[s].label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filter Row + Bulk Actions + Settings */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                        type="text"
                        placeholder="Search tickets..."
                        value={filters.search}
                        onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                    />
                </div>
                <select
                    value={filters.type}
                    onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                    className="text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                >
                    <option value="">All Types</option>
                    {FR_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <select
                    value={filters.priority}
                    onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
                    className="text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                >
                    <option value="">All Priorities</option>
                    {FR_PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
                <select
                    value={filters.category}
                    onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
                    className="text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                >
                    <option value="">All Categories</option>
                    {FR_CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </select>

                {/* Bulk actions */}
                {selectedIds.size > 0 && (
                    <div className="flex items-center gap-2 ml-auto">
                        <span className="text-xs text-stone-500">{selectedIds.size} selected</span>
                        <select
                            value={bulkStatus}
                            onChange={e => setBulkStatus(e.target.value)}
                            className="text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white"
                        >
                            <option value="">Move to...</option>
                            {FR_STATUSES.map(s => <option key={s} value={s}>{FR_STATUS_LABELS[s].label}</option>)}
                        </select>
                        <Button size="sm" variant="outline" onClick={bulkChangeStatus} disabled={!bulkStatus}>
                            <Check className="w-3 h-3 mr-1" /> Apply
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                )}

                <Button size="sm" variant="outline" className="ml-auto" onClick={() => { loadSettings(); setSettingsOpen(true); }}>
                    <Settings className="w-3.5 h-3.5 mr-1" /> Settings
                </Button>
            </div>

            {/* Kanban Board */}
            {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
            ) : (
                <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
                    {columns.map(col => (
                        <div key={col.status} className="flex-shrink-0 w-[280px] flex flex-col">
                            {/* Column Header */}
                            <div className={`px-3 py-2 rounded-t-xl ${col.headerBg} ${col.border} border-b-2 flex items-center justify-between`}>
                                <span className={`text-xs font-semibold font-[Manrope] ${col.headerText}`}>{col.label}</span>
                                <span className={`text-xs font-bold ${col.headerText}`}>{col.items.length}</span>
                            </div>
                            {/* Column Body */}
                            <div className="flex-1 bg-stone-50/50 border border-t-0 border-stone-200 rounded-b-xl p-2 space-y-2 overflow-y-auto" style={{ maxHeight: '600px' }}>
                                {col.items.length === 0 && (
                                    <div className="text-center py-8 text-stone-300 text-xs">No items</div>
                                )}
                                {col.items.map(req => (
                                    <KanbanCard
                                        key={req.request_id}
                                        req={req}
                                        isSelected={selectedIds.has(req.request_id)}
                                        isExpanded={selectedCard === req.request_id}
                                        onToggleSelect={() => toggleSelect(req.request_id)}
                                        onClickCard={() => setSelectedCard(selectedCard === req.request_id ? null : req.request_id)}
                                        onChangeStatus={changeStatus}
                                        onChangePriority={changePriority}
                                        onChangeCategory={changeCategory}
                                        onAssign={assignTo}
                                        onDelete={deleteRequest}
                                        adminUsers={adminUsers}
                                        adminNotes={adminNotes}
                                        setAdminNotes={setAdminNotes}
                                        onSaveNotes={saveAdminNotes}
                                        savingNotes={savingNotes}
                                        onDispatch={() => openDispatchModal(req)}
                                        attachments={attachmentsCache[req.request_id]}
                                        loadingAttachments={loadingAttachments === req.request_id}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Settings Modal */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-[Manrope]">Notification Settings</DialogTitle>
                        <DialogDescription>Configure how you receive feature request notifications.</DialogDescription>
                    </DialogHeader>
                    {settings && (
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-stone-700">Notification Emails</label>
                                <div className="space-y-1">
                                    {(settings.notification_emails || []).map((email, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="flex-1 text-xs text-stone-600 bg-stone-50 px-2 py-1 rounded">{email}</span>
                                            <button
                                                onClick={() => setSettings(s => ({ ...s, notification_emails: s.notification_emails.filter((_, idx) => idx !== i) }))}
                                                className="text-stone-400 hover:text-red-500"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        placeholder="Add email..."
                                        id="settings-new-email"
                                        className="flex-1 text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && e.target.value) {
                                                setSettings(s => ({ ...s, notification_emails: [...(s.notification_emails || []), e.target.value] }));
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                    <Button size="sm" variant="outline" onClick={() => {
                                        const input = document.getElementById('settings-new-email');
                                        if (input?.value) {
                                            setSettings(s => ({ ...s, notification_emails: [...(s.notification_emails || []), input.value] }));
                                            input.value = '';
                                        }
                                    }}>
                                        <Plus className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="text-sm text-stone-700">Notify on new submissions</label>
                                <button
                                    onClick={() => setSettings(s => ({ ...s, notify_on_new_submission: !s.notify_on_new_submission }))}
                                    className={`w-10 h-5 rounded-full transition-colors ${settings.notify_on_new_submission ? 'bg-stone-900' : 'bg-stone-300'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.notify_on_new_submission ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="text-sm text-stone-700">Notify on critical bugs</label>
                                <button
                                    onClick={() => setSettings(s => ({ ...s, notify_on_critical_bugs: !s.notify_on_critical_bugs }))}
                                    className={`w-10 h-5 rounded-full transition-colors ${settings.notify_on_critical_bugs ? 'bg-stone-900' : 'bg-stone-300'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.notify_on_critical_bugs ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
                        <Button onClick={saveSettings}>Save Settings</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v2.16.36 Magic Dispatch modal */}
            <Dialog
                open={!!dispatchingId}
                onOpenChange={(o) => { if (!o) { setDispatchingId(null); setExtraContext(''); } }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-[Manrope]">Dispatch to Claude Code</DialogTitle>
                        <DialogDescription>
                            Fires a cloud Claude Code session that works the ticket autonomously and opens a PR.
                            The agent re-fetches canonical ticket details from the tracker MCP.
                        </DialogDescription>
                    </DialogHeader>
                    {(() => {
                        const req = requests.find(r => r.request_id === dispatchingId);
                        if (!req) return null;
                        return (
                            <div className="space-y-3 py-2">
                                <div className="bg-stone-50 p-3 rounded-lg text-xs">
                                    <div className="font-mono text-stone-500">{req.ticket_number}</div>
                                    <div className="font-semibold text-stone-800 mt-0.5">{req.title}</div>
                                    <p className="text-stone-600 mt-1 whitespace-pre-wrap max-h-32 overflow-y-auto">
                                        {req.description}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-stone-700">
                                        Extra context or guidance <span className="text-stone-400 font-normal">(optional)</span>
                                    </label>
                                    <textarea
                                        value={extraContext}
                                        onChange={e => setExtraContext(e.target.value)}
                                        rows={4}
                                        placeholder="Specific files to look at, constraints, tests to add…"
                                        className="w-full mt-1 text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                        disabled={isDispatching}
                                    />
                                </div>
                                {req.routine_session_url && (
                                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                        Previously dispatched by <b>{req.dispatched_by_name}</b> — dispatching again will start a fresh session.
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDispatchingId(null); setExtraContext(''); }} disabled={isDispatching}>
                            Cancel
                        </Button>
                        <Button onClick={() => dispatchToClaudeCode(false)} disabled={isDispatching}>
                            {isDispatching ? 'Dispatching…' : 'Dispatch'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ─── Kanban Card Sub-component ────────────────────────────────────────── */
function KanbanCard({
    req, isSelected, isExpanded, onToggleSelect, onClickCard,
    onChangeStatus, onChangePriority, onChangeCategory, onAssign, onDelete,
    adminUsers, adminNotes, setAdminNotes, onSaveNotes, savingNotes,
    onDispatch,
    attachments, loadingAttachments,
}) {
    const typeBadge = req.type === 'bug'
        ? 'bg-red-100 text-red-700'
        : 'bg-amber-100 text-amber-700';

    return (
        <div className={`bg-white rounded-lg border transition-all ${isSelected ? 'border-stone-900 ring-1 ring-stone-900/20' : 'border-stone-200 hover:border-stone-300'} ${isExpanded ? 'shadow-md' : 'shadow-sm'}`}>
            {/* Compact Card */}
            <div className="p-2.5">
                <div className="flex items-start gap-2">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onToggleSelect}
                        className="mt-0.5 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20"
                        onClick={e => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={onClickCard}>
                        {/* Top row: ticket + badges */}
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className="text-[10px] font-mono text-stone-400">{req.ticket_number}</span>
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${typeBadge}`}>
                                {req.type === 'bug' ? <Bug className="w-2.5 h-2.5" /> : <Sparkles className="w-2.5 h-2.5" />}
                                {req.type === 'bug' ? 'Bug' : 'Feature'}
                            </span>
                            {req.priority && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${FR_PRIORITY_COLORS[req.priority] || 'bg-stone-100 text-stone-600'}`}>
                                    {req.priority.charAt(0).toUpperCase() + req.priority.slice(1)}
                                </span>
                            )}
                            {req.category && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-stone-100 text-stone-500">
                                    {req.category}
                                </span>
                            )}
                        </div>
                        {/* Title */}
                        <h4 className="font-semibold text-stone-900 text-xs leading-snug line-clamp-2">{req.title}</h4>
                        {/* Bottom row: meta */}
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-stone-400">
                            <span className="flex items-center gap-0.5">
                                <ThumbsUp className="w-2.5 h-2.5" /> {req.vote_count || 0}
                            </span>
                            <span className="flex items-center gap-0.5">
                                <MessageSquare className="w-2.5 h-2.5" /> {req.comment_count || 0}
                            </span>
                            {req.attachment_count > 0 && (
                                <span className="flex items-center gap-0.5">
                                    <Paperclip className="w-2.5 h-2.5" /> {req.attachment_count}
                                </span>
                            )}
                            <span className="ml-auto truncate max-w-[80px]">{req.author_name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-400">
                            <span className="flex items-center gap-0.5">
                                <User className="w-2.5 h-2.5" />
                                {req.assigned_to_name || 'Unassigned'}
                            </span>
                            <span className="flex items-center gap-0.5 ml-auto">
                                <Clock className="w-2.5 h-2.5" />
                                {new Date(req.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Detail Panel */}
            {isExpanded && (
                <div className="border-t border-stone-200 p-3 space-y-3 bg-stone-50/50 rounded-b-lg">
                    {/* Full description */}
                    <div>
                        <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Description</label>
                        <p className="text-xs text-stone-700 mt-0.5 whitespace-pre-wrap">{req.description || 'No description provided.'}</p>
                    </div>

                    {/* v2.16.39 BUG-031: Screenshots — lazy-loaded on expand. */}
                    {(req.attachment_count > 0 || (attachments && attachments.length > 0)) && (
                        <div>
                            <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1">
                                <Paperclip className="w-2.5 h-2.5" />
                                Screenshots {attachments?.length > 0 ? `(${attachments.length})` : ''}
                            </label>
                            {loadingAttachments ? (
                                <div className="mt-1 text-[11px] text-stone-400 italic">Loading…</div>
                            ) : (attachments && attachments.length > 0) ? (
                                <div className="mt-1 grid grid-cols-2 gap-2">
                                    {attachments.map((att, i) => (
                                        <a
                                            key={att.attachment_id || i}
                                            href={att.data_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={e => e.stopPropagation()}
                                            className="block rounded overflow-hidden border border-stone-200 hover:border-stone-400 transition-colors"
                                            title="Click to open full size in a new tab"
                                        >
                                            <img
                                                src={att.data_url}
                                                alt={`Screenshot ${i + 1}`}
                                                className="w-full h-auto block"
                                                loading="lazy"
                                            />
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-1 text-[11px] text-stone-400 italic">No screenshots attached.</div>
                            )}
                        </div>
                    )}

                    {/* Admin Notes */}
                    <div>
                        <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Admin Notes</label>
                        <textarea
                            value={adminNotes[req.request_id] || ''}
                            onChange={e => setAdminNotes(prev => ({ ...prev, [req.request_id]: e.target.value }))}
                            onBlur={() => onSaveNotes(req.request_id)}
                            rows={2}
                            placeholder="Internal notes..."
                            className="w-full mt-0.5 text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none"
                        />
                        {savingNotes === req.request_id && <span className="text-[10px] text-stone-400">Saving...</span>}
                    </div>

                    {/* v2.16.36 Claude Code dispatch section */}
                    <div>
                        <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Claude Code</label>
                        {req.routine_session_url ? (
                            <div className="mt-1 flex items-center gap-2 text-xs">
                                <a
                                    href={req.routine_session_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline flex items-center gap-1"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <Sparkles className="w-3 h-3" /> View session
                                </a>
                                <span className="text-stone-400">
                                    · {req.dispatched_by_name || 'Admin'}
                                    {req.dispatched_at ? ` · ${new Date(req.dispatched_at).toLocaleDateString()}` : ''}
                                </span>
                                <button
                                    onClick={e => { e.stopPropagation(); onDispatch?.(); }}
                                    className="ml-auto text-[10px] px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200 text-stone-700"
                                >
                                    Re-dispatch
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={e => { e.stopPropagation(); onDispatch?.(); }}
                                className="mt-1 w-full text-xs px-3 py-1.5 rounded-lg bg-stone-900 text-white hover:bg-stone-700 flex items-center justify-center gap-1.5"
                            >
                                <Sparkles className="w-3 h-3" /> Dispatch to Claude Code
                            </button>
                        )}
                        {req.github_issue_url && (
                            <a
                                href={req.github_issue_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="mt-1 inline-block text-[10px] text-stone-400 hover:text-stone-600 hover:underline"
                            >
                                GitHub: #{req.github_issue_number}
                            </a>
                        )}
                    </div>

                    {/* Controls grid */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Status</label>
                            <select
                                value={req.status}
                                onChange={e => onChangeStatus(req.request_id, e.target.value)}
                                className="w-full mt-0.5 text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                            >
                                {FR_STATUSES.map(s => <option key={s} value={s}>{FR_STATUS_LABELS[s].label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Priority</label>
                            <select
                                value={req.priority || ''}
                                onChange={e => onChangePriority(req.request_id, e.target.value)}
                                className="w-full mt-0.5 text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                            >
                                <option value="">None</option>
                                {FR_PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Category</label>
                            <select
                                value={req.category || ''}
                                onChange={e => onChangeCategory(req.request_id, e.target.value)}
                                className="w-full mt-0.5 text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                            >
                                <option value="">None</option>
                                {FR_CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Assign To</label>
                            <select
                                value={req.assigned_to || ''}
                                onChange={e => {
                                    const user = adminUsers.find(u => u.user_id === e.target.value);
                                    onAssign(req.request_id, e.target.value || null, user?.name || null);
                                }}
                                className="w-full mt-0.5 text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                            >
                                <option value="">Unassigned</option>
                                {adminUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Meta + Delete */}
                    <div className="flex items-center justify-between pt-1 border-t border-stone-200">
                        <div className="text-[10px] text-stone-400">
                            by {req.author_name} ({req.author_email}) &middot; {new Date(req.created_at).toLocaleDateString()}
                            {req.released_at && <> &middot; Released {new Date(req.released_at).toLocaleDateString()}</>}
                        </div>
                        <button
                            onClick={() => onDelete(req.request_id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
